import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faBan,
  faBuilding,
  faBuildingShield,
  faCalendarCheck,
  faCalendarDay,
  faCalendarWeek,
  faChartColumn,
  faChevronRight,
  faCircleCheck,
  faCircleExclamation,
  faClock,
  faDiagramProject,
  faKey
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../context/AuthContext';
import { useAssistantName } from '../../queries/agents';
import { usePhone } from '../../hooks/usePhone';
import DailyBars from '../Reports/charts/DailyBars';
import { useHomeData } from './useHomeData';
import * as S from './home.styled';

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  return h < 18 ? 'afternoon' : 'evening';
};

const EVENT_ICON = { tour: faKey, appointment: faCalendarCheck, block: faBan };
const STAGE_SHORT = {
  Lead: 'leads',
  'Prequalification Complete': 'prequalified',
  'Tour Scheduled': 'showings',
  'Application Submitted': 'applications',
  'Background Check Submitted': 'screening'
};

const unit = (n, word) => (n === 1 && word.endsWith('s') ? word.slice(0, -1) : word);

function Kpi({ label, value, cue, loading, onClick }) {
  return (
    <S.Kpi type="button" onClick={onClick} aria-label={`${label}: ${value}. Open the list.`}>
      <div className="k-label">{label}</div>
      <div className="k-value">{loading ? '—' : value}</div>
      <div className="k-sub">
        <span>{cue}</span>
        <FontAwesomeIcon icon={faArrowRight} />
      </div>
    </S.Kpi>
  );
}

function SmallCard({ icon, color, title, cta, onCta, big, sub, loading }) {
  return (
    <S.SmallCard>
      <div className="sc-head">
        <S.Bubble $color={color}>
          <FontAwesomeIcon icon={icon} />
        </S.Bubble>
        <span className="sc-title">{title}</span>
        <S.HeadLink type="button" onClick={onCta}>
          {cta}
        </S.HeadLink>
      </div>
      <div className="sc-big">{loading ? '—' : big}</div>
      <div className="sc-sub">{sub}</div>
    </S.SmallCard>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const { user, can } = useAuth();
  const assistant = useAssistantName();
  const data = useHomeData(can);
  const phone = usePhone();
  const { recap, pointer, portfolio, today, leasing, week, trend, coming, tz } = data;

  const go = (path) => () => navigate(path);
  const firstName = (user?.firstName || user?.fullName || 'there').split(/\s+/)[0];

  const pTone = pointer.top?.aging?.state === 'red' ? 'red' : pointer.needs + pointer.review ? 'amber' : 'ok';
  const pCount = pointer.needs + pointer.review;

  const todaySub = today.events.length
    ? today.events
        .slice(0, 2)
        .map((e) => `${e.time} ${e.typeLabel.toLowerCase()}`)
        .join(' · ')
    : 'Nothing booked';
  const leasingSub = leasing.stages.length
    ? leasing.stages
        .slice(0, 3)
        .map((s) => `${s.count} ${unit(s.count, STAGE_SHORT[s.key] || s.key.toLowerCase())}`)
        .join(' · ')
    : 'No active leads';

  const weekDays = phone ? week.days.filter((iso) => iso === week.today || week.events.some((e) => e.date === iso)) : week.days;
  const dow = (iso) => dayjs(iso).format('ddd');
  const dnum = (iso) => dayjs(iso).format('D');

  return (
    <S.Page>
      {/* Hero */}
      <S.Hero>
        <S.Glow />
        <S.HeroIntro>
          <S.Eyebrow>
            <FontAwesomeIcon icon={faBuildingShield} />
            <span>{dayjs().format('dddd, MMMM D')}</span>
          </S.Eyebrow>
          <h2>{`Good ${greeting()}, ${firstName}.`}</h2>
          <S.HeroSub>{`Here's what ${assistant} handled in the last 24 hours.`}</S.HeroSub>
        </S.HeroIntro>
        <S.Recap>
          <Kpi label="Calls answered" value={recap.calls} cue="Call Logs" loading={recap.loading} onClick={go('/calls')} />
          <Kpi label="Emergencies" value={recap.emergencies} cue="Call Logs" loading={recap.loading} onClick={go('/calls')} />
          <Kpi label="Tour requests" value={recap.tours} cue="Schedule" loading={recap.loading} onClick={go('/scheduling')} />
          <Kpi label="Work orders" value={recap.workOrders} cue="Maintenance" loading={recap.loading} onClick={go('/pipelines?tab=maintenance')} />
        </S.Recap>
      </S.Hero>

      {/* Pointer */}
      <S.Pointer type="button" $tone={pTone} onClick={go('/todos')}>
        <FontAwesomeIcon icon={pTone === 'ok' ? faCircleCheck : faCircleExclamation} />
        <div className="p-grow">
          <div className="p-title">
            {pCount
              ? `${pointer.needs} need you and ${pointer.review} need review in Agent Actions`
              : 'Nothing needs you in Agent Actions.'}
          </div>
          {pointer.top && <div className="p-sub">Most urgent: {pointer.top.title}</div>}
        </div>
        <span className="p-open">
          Open <FontAwesomeIcon icon={faChevronRight} />
        </span>
      </S.Pointer>

      {/* Small cards */}
      <S.SmallGrid>
        <SmallCard
          icon={faBuilding}
          color={undefined}
          title="Portfolio"
          cta="Open"
          onCta={go('/property')}
          loading={portfolio.loading}
          big={`${portfolio.doors} ${unit(portfolio.doors, 'doors')}`}
          sub={`${portfolio.listed} listed · ${portfolio.vacant} vacant`}
        />
        <SmallCard
          icon={faCalendarDay}
          title="Today"
          cta="Calendar"
          onCta={go('/scheduling')}
          loading={today.loading}
          big={`${today.count} on the calendar`}
          sub={todaySub}
        />
        <SmallCard
          icon={faDiagramProject}
          title="Leasing"
          cta="Pipeline"
          onCta={go('/pipelines?tab=leasing')}
          loading={leasing.loading}
          big={`${leasing.count} in the pipeline`}
          sub={leasingSub}
        />
      </S.SmallGrid>

      {/* This week */}
      <S.Section>
        <S.SectionHead>
          <S.Bubble>
            <FontAwesomeIcon icon={faCalendarWeek} />
          </S.Bubble>
          <h3>This week</h3>
          <S.HeadLink type="button" onClick={go('/scheduling')}>
            Open calendar
          </S.HeadLink>
        </S.SectionHead>
        <S.Week>
          {weekDays.map((iso) => {
            const list = week.events.filter((e) => e.date === iso);
            const isToday = iso === week.today;
            return (
              <S.WeekDay key={iso} $today={isToday}>
                <button type="button" className="wd-head" onClick={go('/scheduling')}>
                  <span className="wd-dow">{dow(iso)}</span>
                  <span className="wd-num">{dnum(iso)}</span>
                  {isToday && <span className="wd-today">Today</span>}
                </button>
                {list.length ? (
                  list.map((e) => (
                    <S.EventChip key={e.id} type="button" $color={e.color} onClick={go('/scheduling')}>
                      <FontAwesomeIcon icon={EVENT_ICON[e.type] || faCalendarCheck} />
                      <span className="ev-time">{e.time}</span>
                      <span className="ev-title">{e.title}</span>
                    </S.EventChip>
                  ))
                ) : (
                  <span className="wd-empty">Nothing booked</span>
                )}
              </S.WeekDay>
            );
          })}
        </S.Week>
      </S.Section>

      {/* Trend + Coming up */}
      <S.TwoCol>
        <S.Section>
          <S.SectionHead>
            <S.Bubble>
              <FontAwesomeIcon icon={faChartColumn} />
            </S.Bubble>
            <h3>{phone ? 'Calls, last 14 days' : 'Calls per day, last 14 days'}</h3>
            {data.canReports && (
              <S.HeadLink type="button" onClick={go('/reports')}>
                Analytics
              </S.HeadLink>
            )}
          </S.SectionHead>
          {trend.series.length ? (
            <DailyBars series={trend.series} height={220} />
          ) : (
            <S.Muted $pad>{trend.loading ? 'Loading…' : 'No calls in this range.'}</S.Muted>
          )}
        </S.Section>

        <S.Section>
          <S.SectionHead>
            <S.Bubble>
              <FontAwesomeIcon icon={faClock} />
            </S.Bubble>
            <h3>Coming up</h3>
          </S.SectionHead>
          {coming.rows.length ? (
            coming.rows.map((e) => (
              <S.ComingRow key={e.id} type="button" onClick={go('/scheduling')}>
                <span className="cr-when">
                  {(e.date === week.today ? 'Today' : dayjs(e.start).tz(tz).format('ddd')) + ' ' + e.time}
                </span>
                <span className="cr-title">{e.title}</span>
              </S.ComingRow>
            ))
          ) : (
            <S.Muted $pad>{coming.loading ? 'Loading…' : 'Nothing scheduled.'}</S.Muted>
          )}
        </S.Section>
      </S.TwoCol>
    </S.Page>
  );
}
